/** Digit with role metadata for rounding-to-significant-figures visualisation. */
export interface SfDigit {
  readonly char: string;
  readonly role: 'leading-zero' | 'point' | 'keep' | 'check' | 'discard' | 'negative';
  /** 1-based sig-fig position (only set for role 'keep' and 'check'). */
  readonly sfIndex?: number;
}

export interface SfAnswerDigit {
  readonly char: string;
  readonly isPoint: boolean;
  readonly changed: boolean;
}

export interface SfBreakdown {
  readonly digits: SfDigit[];
  readonly answerDigits: SfAnswerDigit[];
  readonly totalSigFigs: number;
  readonly rounded: string;
  readonly checkDigitValue: number;
  readonly roundedUp: boolean;
}

/**
 * Break a number into per-character roles for rounding to `sf` significant figures.
 *
 * Roles:
 *  - `negative`      the minus sign (if present)
 *  - `leading-zero`  zeros before the first non-zero digit (including the "0." prefix)
 *  - `point`         the decimal point character
 *  - `keep`          the first `sf` significant digits (pink)
 *  - `check`         the very next significant-position digit (indigo)
 *  - `discard`       remaining integer digits after check (grey, only for integers)
 */
export function breakdownSf(value: number, sf: number): SfBreakdown {
  const abs = Math.abs(value);
  const isNegative = value < 0;

  // Get a string with enough precision
  const precision = Math.max(sf + 4, 12);
  const raw = abs.toPrecision(precision);

  const digits: SfDigit[] = [];
  let sigCount = 0;
  let foundFirstNonZero = false;
  let passedDecimalPoint = false;
  let reachedCheck = false;

  if (isNegative) {
    digits.push({ char: '-', role: 'negative' });
  }

  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];

    if (ch === '.') {
      if (reachedCheck) {
        // Past check digit in integer part, decimal means we're done
        break;
      }
      passedDecimalPoint = true;
      digits.push({ char: '.', role: 'point' });
      continue;
    }

    if (ch === 'e' || ch === 'E') {
      break;
    }

    if (!foundFirstNonZero && ch === '0') {
      digits.push({ char: ch, role: 'leading-zero' });
      continue;
    }

    foundFirstNonZero = true;
    sigCount++;

    if (sigCount <= sf) {
      digits.push({ char: ch, role: 'keep', sfIndex: sigCount });
    } else if (sigCount === sf + 1) {
      digits.push({ char: ch, role: 'check', sfIndex: sigCount });
      reachedCheck = true;
      if (passedDecimalPoint) {
        // Check digit is in decimal part — stop here
        break;
      }
    } else {
      // Remaining integer digits after check
      digits.push({ char: ch, role: 'discard' });
    }
  }

  const checkDigitValue = getCheckDigitValue(abs, sf);
  const roundedUp = checkDigitValue >= 5;
  const rounded = formatRoundedSf(value, sf);

  // Build answer digits aligned to the same grid columns
  const answerChars = rounded.split('');
  const answerDigits: SfAnswerDigit[] = [];
  for (let i = 0; i < digits.length; i++) {
    if (i < answerChars.length) {
      const ch = answerChars[i];
      answerDigits.push({
        char: ch,
        isPoint: ch === '.',
        changed: roundedUp && digits[i].role === 'keep' && ch !== digits[i].char,
      });
    } else {
      answerDigits.push({ char: '', isPoint: false, changed: false });
    }
  }

  return {
    digits,
    answerDigits,
    totalSigFigs: countSigFigs(raw),
    rounded,
    checkDigitValue,
    roundedUp,
  };
}

function getCheckDigitValue(abs: number, sf: number): number {
  if (abs === 0) {
    return 0;
  }

  // Find the magnitude of the first significant digit
  const magnitude = Math.floor(Math.log10(abs));
  // The check digit is at position (magnitude - sf)
  const checkPos = magnitude - sf;
  // Extract that digit
  const shifted = abs / Math.pow(10, checkPos);
  return Math.floor(shifted) % 10;
}

function countSigFigs(raw: string): number {
  let count = 0;
  let foundFirst = false;
  for (const ch of raw) {
    if (ch === '.' || ch === '-') {
      continue;
    }
    if (!foundFirst && ch === '0') {
      continue;
    }
    foundFirst = true;
    count++;
  }
  return count;
}

/** Round a number to `sf` significant figures and format the string. */
export function formatRoundedSf(value: number, sf: number): string {
  if (value === 0) {
    return '0';
  }
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  const magnitude = Math.floor(Math.log10(abs));
  const factor = Math.pow(10, magnitude - sf + 1);
  const rounded = Math.round(abs / factor) * factor;

  // Format: if the original had decimals and result needs them, show them
  if (magnitude < 0) {
    // Small number (0.00xxx)
    const decimalPlaces = sf - magnitude - 1;
    return sign + rounded.toFixed(decimalPlaces);
  } else if (magnitude < sf - 1) {
    // Number like 6.024 to 3 sf -> need decimal places
    const decimalPlaces = sf - magnitude - 1;
    return sign + rounded.toFixed(decimalPlaces);
  } else {
    // Large number, no decimals needed
    return sign + Math.round(rounded).toString();
  }
}

/** Generate a random number suitable for s.f. rounding practice. */
export function randomSfNumber(): number {
  const templates = [
    () => +(Math.random() * 0.01).toFixed(6), // 0.00xxxx
    () => +(Math.random() * 0.1).toFixed(5), // 0.0xxxx
    () => +(Math.random() * 100).toFixed(3), // xx.xxx
    () => Math.floor(Math.random() * 90000) + 10000, // xxxxx (5-digit integer)
    () => +(Math.random() * 10).toFixed(4), // x.xxxx
    () => Math.floor(Math.random() * 9000) + 1000, // xxxx
  ];
  const fn = templates[Math.floor(Math.random() * templates.length)];
  let result = fn();
  // Avoid exact zero
  while (result === 0) {
    result = fn();
  }
  return result;
}
